<template>
    <div>
        <template v-if="hasSubscription">
            <Label class="text-sm font-medium text-iron-700">
                {{ $t("event_types.calendar_title.label") }}
            </Label>
            <p class="text-sm text-iron-500 mt-0.5 tracking-[-0.01em]">
                {{ $t("event_types.calendar_title.description") }}
            </p>
        </template>

        <div v-if="!hasSubscription" class="mt-2">
            <PromoBanner feature="calendar_event_name">
                <template #title>
                    {{ $t("event_types.calendar_title.locked_title") }}
                </template>
                <template #description>
                    {{ $t("event_types.calendar_title.locked_description") }}
                </template>
                <template #icon>
                    <Lock :size="20" class="text-[#E92F34]" />
                </template>
            </PromoBanner>
        </div>

        <Dialog v-else v-model:open="dialogOpen">
            <DialogTrigger as-child>
                <button
                    type="button"
                    class="mt-2 rounded-md border-iron-300 bg-white px-3 py-2 text-sm text-iron-700 hover:bg-iron-50 flex w-full items-center justify-between border transition-colors"
                >
                    <span
                        class="min-w-0 truncate"
                        :class="
                            displayValue ? 'text-iron-700' : 'text-iron-400'
                        "
                    >
                        {{
                            displayValue ||
                            $t("event_types.calendar_title.trigger_placeholder")
                        }}
                    </span>
                    <Pencil :size="14" class="text-iron-500 shrink-0" />
                </button>
            </DialogTrigger>

            <DialogContent class="sm:max-w-[550px]">
                <DialogHeader>
                    <DialogTitle>
                        {{ $t("event_types.calendar_title.dialog_title") }}
                    </DialogTitle>
                    <DialogDescription>
                        {{
                            $t("event_types.calendar_title.dialog_description")
                        }}
                    </DialogDescription>
                </DialogHeader>

                <div class="space-y-5 py-2">
                    <div>
                        <Label class="text-sm font-medium">
                            {{
                                $t("event_types.calendar_title.template_label")
                            }}
                        </Label>
                        <Input
                            ref="templateInput"
                            v-model="localTemplate"
                            class="mt-1.5"
                            :placeholder="eventTypeName"
                        />
                    </div>

                    <div>
                        <p class="text-sm font-medium text-iron-700 mb-2">
                            {{
                                $t(
                                    "event_types.calendar_title.available_variables",
                                )
                            }}
                        </p>
                        <div class="gap-1.5 flex flex-wrap">
                            <button
                                v-for="variable in builtInVariables"
                                :key="variable.name"
                                type="button"
                                @click="insertVariable(variable.name)"
                            >
                                <Badge
                                    variant="secondary"
                                    class="hover:bg-stone-200 cursor-pointer transition-colors"
                                >
                                    {{ "{" + variable.name + "}" }}
                                </Badge>
                            </button>
                        </div>
                        <button
                            type="button"
                            class="mt-2 gap-1 text-xs text-iron-500 hover:text-iron-700 flex items-center transition-colors"
                            @click="showDescriptions = !showDescriptions"
                        >
                            <ChevronDown
                                :size="14"
                                class="transition-transform duration-200"
                                :class="{ '-rotate-90': !showDescriptions }"
                            />
                            {{
                                showDescriptions
                                    ? $t(
                                          "event_types.calendar_title.hide_descriptions",
                                      )
                                    : $t(
                                          "event_types.calendar_title.show_descriptions",
                                      )
                            }}
                        </button>
                        <div v-show="showDescriptions" class="mt-2 space-y-1">
                            <p
                                v-for="variable in builtInVariables"
                                :key="variable.name"
                                class="text-xs text-iron-500"
                            >
                                <span class="font-medium text-iron-600">{{
                                    "{" + variable.name + "}"
                                }}</span>
                                &mdash; {{ variable.description }}
                            </p>
                        </div>
                    </div>

                    <div v-if="customFieldVariables.length > 0">
                        <p class="text-sm font-medium text-iron-700 mb-2">
                            {{
                                $t(
                                    "event_types.calendar_title.booking_question_variables",
                                )
                            }}
                        </p>
                        <div class="gap-1.5 flex flex-wrap">
                            <button
                                v-for="variable in customFieldVariables"
                                :key="variable.name"
                                type="button"
                                @click="insertVariable(variable.name)"
                            >
                                <Badge
                                    variant="secondary"
                                    class="hover:bg-stone-200 cursor-pointer transition-colors"
                                >
                                    {{ "{" + variable.name + "}" }}
                                </Badge>
                            </button>
                        </div>
                    </div>

                    <div>
                        <p class="text-sm font-medium text-iron-700 mb-2">
                            {{ $t("event_types.calendar_title.preview") }}
                        </p>
                        <div
                            class="rounded-lg border-iron-200 bg-iron-50 p-4 border"
                        >
                            <div
                                class="rounded bg-blue-100 border-blue-200 px-2 py-1.5 inline-block border"
                                style="width: 160px"
                            >
                                <p
                                    class="text-xs font-medium text-blue-700 leading-tight line-clamp-3 break-words"
                                >
                                    {{ previewTitle }}
                                </p>
                                <p
                                    class="text-blue-500 leading-tight mt-0.5 text-[10px]"
                                >
                                    9:00 - 9:30
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <DialogClose as-child>
                        <Button variant="outline" @click="onCancel">
                            {{ $t("cancel") }}
                        </Button>
                    </DialogClose>
                    <Button @click="onSave">
                        {{ $t("save") }}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";
import { Pencil, ChevronDown, Lock } from "lucide-vue-next";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useEventTypeStore } from "../../../stores/eventTypeStore";
import PromoBanner from "../../../../Atomic/PromoBanner.vue";
import type { CustomField } from "../../../types/EventType";

interface Props {
    eventTypeName: string;
    customFields: CustomField[];
    hasSubscription: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();
const store = useEventTypeStore();

// @ts-expect-error
const userName = computed(() => usePage().props.user?.name ?? "John Doe");

const dialogOpen = ref(false);
const localTemplate = ref("");
const showDescriptions = ref(false);
const templateInput = ref<InstanceType<typeof Input> | null>(null);

const displayValue = computed(() => store.calendarTitle || "");

const builtInVariables = computed(() => [
    {
        name: "Event type title",
        description: t("event_types.calendar_title.event_type_title_desc"),
    },
    {
        name: "Event duration",
        description: t("event_types.calendar_title.event_duration_desc"),
    },
    {
        name: "Organizer",
        description: t("event_types.calendar_title.organiser_desc"),
    },
    {
        name: "Organizer first name",
        description: t("event_types.calendar_title.organiser_first_name_desc"),
    },
    {
        name: "Attendee",
        description: t("event_types.calendar_title.attendee_desc"),
    },
    {
        name: "Attendee first name",
        description: t("event_types.calendar_title.attendee_first_name_desc"),
    },
    {
        name: "Location",
        description: t("event_types.calendar_title.location_desc"),
    },
]);

const customFieldVariables = computed(() => {
    return props.customFields
        .filter(
            (cf) =>
                !["EMAIL", "PHONE", "FIRST_NAME", "LAST_NAME"].includes(
                    cf.merge_tag,
                ),
        )
        .filter((cf) => cf.deleted_at === null)
        .map((cf) => ({
            name: cf.name,
            description: cf.label,
        }));
});

const previewTitle = computed(() => {
    const template = localTemplate.value || props.eventTypeName;
    return template
        .replace("{Event type title}", props.eventTypeName)
        .replace("{Event duration}", "30 min")
        .replace("{Organizer}", userName.value)
        .replace(
            "{Organizer first name}",
            userName.value.split(" ")[0] ?? userName.value,
        )
        .replace("{Attendee}", userName.value)
        .replace(
            "{Attendee first name}",
            userName.value.split(" ")[0] ?? userName.value,
        )
        .replace("{Location}", "Google Meet");
});

function insertVariable(name: string) {
    const el = templateInput.value?.$el as HTMLInputElement | undefined;
    const insertion = `{${name}}`;

    if (el) {
        const start = el.selectionStart ?? localTemplate.value.length;
        const end = el.selectionEnd ?? start;
        const before = localTemplate.value.slice(0, start);
        const after = localTemplate.value.slice(end);
        localTemplate.value = before + insertion + after;

        nextTick(() => {
            const cursorPos = start + insertion.length;
            el.focus();
            el.setSelectionRange(cursorPos, cursorPos);
        });
    } else {
        localTemplate.value += insertion;
    }
}

function onCancel() {
    dialogOpen.value = false;
}

function onSave() {
    store.calendarTitle = localTemplate.value || undefined;
    dialogOpen.value = false;
}

watch(dialogOpen, (isOpen) => {
    if (isOpen) {
        localTemplate.value = store.calendarTitle ?? "";
    }
});
</script>
